import {
  ANIMATION_DELIVERY_LIMITS,
  blobToBase64,
  browserIsTypeSupported,
  contactSheetTimestamps,
  createDomCanvas,
  RenderCancelledError,
  renderContactSheetPng,
  renderFramePng,
  renderReviewVideo,
  RenderTimeoutError,
  resolveSceneTiming,
  resolveVideoRange,
  selectVideoFormat,
  UnsupportedMediaError,
  validateTimestamp,
  type AnimationRenderScene,
  type CreateCanvas,
  type FrameRenderer,
  type IsTypeSupported,
  type VideoContainer,
} from '../../animationRender';
import { getPreviewArtifactStore, type PreviewArtifactStore } from '../artifactStore';
import { describeCaptureFailure } from '../capturePreviewImage';
import { getTemplateFrameRenderer } from '../previewCaptureRegistry';
import {
  mcpToolResult,
  toolError,
  toolSuccess,
  type TemplateWebMCPFailure,
  type TemplateWebMCPToolResult,
} from '../responses';
import { readWorkingRevision } from '../revision';
import { readAnimation } from './read';

export const ANIMATION_PREVIEW_MODES = ['frame', 'contact_sheet', 'video'] as const;
export type AnimationPreviewMode = (typeof ANIMATION_PREVIEW_MODES)[number];

export const ANIMATION_PREVIEW_TIMEOUT_MS: Record<AnimationPreviewMode, number> = {
  frame: 15_000,
  contact_sheet: 45_000,
  video: 120_000,
};

const COMMON_ARGS = ['mode', 'expectedWorkingRevision', 'failOnAssetWarnings'];
const ARGS_BY_MODE: Record<AnimationPreviewMode, string[]> = {
  frame: [...COMMON_ARGS, 'timeSeconds'],
  contact_sheet: [...COMMON_ARGS, 'timestamps'],
  video: [...COMMON_ARGS, 'startSeconds', 'endSeconds', 'fps', 'format', 'scale'],
};

export function isAnimationPreviewMode(value: unknown): value is AnimationPreviewMode {
  return (ANIMATION_PREVIEW_MODES as readonly unknown[]).includes(value);
}

export type AnimationPreviewDependencies = {
  renderer: FrameRenderer | null;
  createCanvas: CreateCanvas;
  isTypeSupported: IsTypeSupported | null;
  artifactStore: PreviewArtifactStore | null;
  MediaRecorderImpl?: Parameters<typeof renderReviewVideo>[3]['MediaRecorderImpl'];
};

export function browserPreviewDependencies(): AnimationPreviewDependencies {
  return {
    renderer: getTemplateFrameRenderer(),
    createCanvas: createDomCanvas,
    isTypeSupported: browserIsTypeSupported(),
    artifactStore: getPreviewArtifactStore(),
  };
}

type PreviewResult = TemplateWebMCPToolResult<Record<string, unknown>>;

function failure(...args: Parameters<typeof toolError>): PreviewResult {
  return mcpToolResult(toolError(...args));
}

/** Abort the render on timeout or tool cancellation, and stop waiting for a hung step. */
async function withRenderDeadline<T>(
  timeoutMs: number,
  parentSignal: AbortSignal | undefined,
  work: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const aborted = new Promise<never>((_, reject) => {
    controller.signal.addEventListener('abort', () => reject(controller.signal.reason), {
      once: true,
    });
  });
  const timer = setTimeout(
    () => controller.abort(new RenderTimeoutError(timeoutMs / 1000)),
    timeoutMs,
  );
  const onParentAbort = () => controller.abort(new RenderCancelledError());
  if (parentSignal?.aborted) {
    onParentAbort();
  } else {
    parentSignal?.addEventListener('abort', onParentAbort, { once: true });
  }
  const running = work(controller.signal);
  void running.catch(() => undefined);
  try {
    return await Promise.race([running, aborted]);
  } finally {
    clearTimeout(timer);
    parentSignal?.removeEventListener('abort', onParentAbort);
  }
}

function isAssetFailure(error: unknown): boolean {
  return typeof Event !== 'undefined' && error instanceof Event;
}

function renderFailure(error: unknown, parentSignal?: AbortSignal): PreviewResult {
  if (error instanceof RenderTimeoutError) {
    return failure('PREVIEW_TIMEOUT', error.message);
  }
  if (
    parentSignal?.aborted ||
    error instanceof RenderCancelledError ||
    (error instanceof DOMException && error.name === 'AbortError')
  ) {
    return failure('PREVIEW_CANCELLED', 'The preview was cancelled.');
  }
  if (error instanceof UnsupportedMediaError) {
    return failure('MEDIA_FORMAT_UNSUPPORTED', error.message);
  }
  if (isAssetFailure(error)) {
    return failure('PREVIEW_ASSETS_UNRESOLVED', describeCaptureFailure(error));
  }
  const message = describeCaptureFailure(error);
  if (/not mounted|unmounted/.test(message)) {
    return failure('PREVIEW_NOT_READY', 'The off-screen preview is not mounted.');
  }
  return failure('PREVIEW_FAILED', 'The animation preview could not be rendered.', { message });
}

function unknownArgs(mode: AnimationPreviewMode, args: Record<string, unknown>): string[] {
  return Object.keys(args).filter((key) => !ARGS_BY_MODE[mode].includes(key));
}

function staleRevision(expected: unknown, current: number): TemplateWebMCPFailure | null {
  if (expected === undefined) {
    return null;
  }
  if (typeof expected !== 'number' || !Number.isFinite(expected)) {
    return toolError('INVALID_INPUT', 'expectedWorkingRevision must be a number.');
  }
  if (expected !== current) {
    return toolError(
      'STALE_REVISION',
      `The working copy is at revision ${current}, not ${expected}.`,
      { expectedWorkingRevision: expected, workingRevision: current },
    );
  }
  return null;
}

type RenderedPreview = {
  data: Record<string, unknown>;
  warnings: string[];
  attachment?: Parameters<typeof mcpToolResult>[1];
};

async function renderMode(
  mode: AnimationPreviewMode,
  args: Record<string, unknown>,
  scene: AnimationRenderScene,
  templateId: string,
  deps: AnimationPreviewDependencies & { renderer: FrameRenderer },
  signal: AbortSignal,
): Promise<RenderedPreview | PreviewResult> {
  const timing = resolveSceneTiming(scene.animation);

  if (mode === 'frame') {
    const timeSeconds = validateTimestamp(args.timeSeconds, timing);
    if (typeof timeSeconds !== 'number') {
      return failure('PREVIEW_RANGE_INVALID', timeSeconds.error, {
        durationSeconds: timing.durationSeconds,
      });
    }
    const frame = await renderFramePng(deps.renderer, scene, timeSeconds, { signal });
    return {
      data: {
        timeSeconds,
        durationSeconds: timing.durationSeconds,
        width: frame.width,
        height: frame.height,
        format: 'png',
      },
      warnings: frame.warnings,
      attachment: { base64: await blobToBase64(frame.blob), mimeType: 'image/png' },
    };
  }

  if (mode === 'contact_sheet') {
    const timestamps = contactSheetTimestamps(timing, args.timestamps);
    if (!Array.isArray(timestamps)) {
      return failure('PREVIEW_RANGE_INVALID', timestamps.error, {
        durationSeconds: timing.durationSeconds,
      });
    }
    const sheet = await renderContactSheetPng(deps.renderer, scene, timestamps, {
      signal,
      createCanvas: deps.createCanvas,
    });
    return {
      data: {
        timestamps: sheet.timestamps,
        frameCount: sheet.timestamps.length,
        durationSeconds: timing.durationSeconds,
        width: sheet.width,
        height: sheet.height,
        frameWidth: sheet.frameWidth,
        frameHeight: sheet.frameHeight,
        format: 'png',
      },
      warnings: sheet.warnings,
      attachment: { base64: await blobToBase64(sheet.blob), mimeType: 'image/png' },
    };
  }

  const range = resolveVideoRange(timing, args);
  if ('error' in range) {
    return failure('PREVIEW_RANGE_INVALID', range.error, {
      durationSeconds: timing.durationSeconds,
    });
  }
  if (args.format !== undefined && args.format !== 'mp4' && args.format !== 'webm') {
    return failure('INVALID_INPUT', 'format must be mp4 or webm.');
  }
  const scale = args.scale ?? 1;
  if (typeof scale !== 'number' || !Number.isFinite(scale) || scale < 0.1 || scale > 1) {
    return failure('INVALID_INPUT', 'scale must be a number from 0.1 to 1.');
  }
  const format = selectVideoFormat(deps.isTypeSupported, args.format as VideoContainer | undefined);
  if (!format) {
    return failure(
      'MEDIA_FORMAT_UNSUPPORTED',
      args.format
        ? `This browser cannot record ${args.format}.`
        : 'This browser cannot record MP4 or WebM.',
    );
  }
  const video = await renderReviewVideo(deps.renderer, scene, range, {
    signal,
    createCanvas: deps.createCanvas,
    format,
    scale,
    MediaRecorderImpl: deps.MediaRecorderImpl,
  });
  const data = {
    mimeType: video.mimeType,
    container: video.container,
    codec: video.codec,
    durationSeconds: video.durationSeconds,
    startSeconds: video.startSeconds,
    endSeconds: video.endSeconds,
    fps: video.fps,
    width: video.width,
    height: video.height,
    byteLength: video.blob.size,
  };
  const fileName = `template-${templateId}-r${scene.revision}.${video.container}`;
  if (video.blob.size <= ANIMATION_DELIVERY_LIMITS.maxEmbeddedBytes) {
    return {
      data: { ...data, delivery: 'embedded' },
      warnings: video.warnings,
      attachment: {
        kind: 'resource',
        uri: `template-builder://preview/${fileName}`,
        base64: await blobToBase64(video.blob),
        mimeType: video.mimeType,
      },
    };
  }
  if (!deps.artifactStore) {
    return failure(
      'ARTIFACT_TOO_LARGE',
      `The video is ${video.blob.size} bytes, over the ${ANIMATION_DELIVERY_LIMITS.maxEmbeddedBytes}-byte embed limit, and no artifact store is available. Lower fps, shorten the range, or set scale.`,
      { byteLength: video.blob.size, maxEmbeddedBytes: ANIMATION_DELIVERY_LIMITS.maxEmbeddedBytes },
    );
  }
  try {
    const upload = await deps.artifactStore.upload(video.blob, {
      mimeType: video.mimeType,
      ttlSeconds: ANIMATION_DELIVERY_LIMITS.artifactTtlSeconds,
      fileName,
      signal,
    });
    if (upload.url.startsWith('blob:')) {
      return failure('ARTIFACT_UPLOAD_FAILED', 'The artifact store returned a browser-local URL.');
    }
    return {
      data: {
        ...data,
        delivery: 'artifact_url',
        artifactUrl: upload.url,
        expiresAt: upload.expiresAt,
      },
      warnings: video.warnings,
    };
  } catch (error) {
    if (signal.aborted) {
      throw error;
    }
    return failure('ARTIFACT_UPLOAD_FAILED', 'The review video could not be uploaded.', {
      message: describeCaptureFailure(error),
    });
  }
}

export async function executeAnimationPreview(input: {
  state: Record<string, unknown>;
  readCurrentState: () => Record<string, unknown> | null | undefined;
  templateId: string;
  args: Record<string, unknown>;
  signal?: AbortSignal;
  dependencies?: AnimationPreviewDependencies;
}): Promise<PreviewResult> {
  const { state, args, signal } = input;
  const mode = args.mode;
  if (!isAnimationPreviewMode(mode)) {
    return failure('INVALID_INPUT', 'preview args.mode must be frame, contact_sheet, or video.');
  }
  const extra = unknownArgs(mode, args);
  if (extra.length > 0) {
    return failure('INVALID_INPUT', `preview ${mode} does not accept ${extra.join(', ')}.`);
  }
  if (args.failOnAssetWarnings !== undefined && typeof args.failOnAssetWarnings !== 'boolean') {
    return failure('INVALID_INPUT', 'failOnAssetWarnings must be a boolean.');
  }
  const revision = readWorkingRevision(state);
  const stale = staleRevision(args.expectedWorkingRevision, revision);
  if (stale) {
    return mcpToolResult(stale);
  }
  const deps = input.dependencies ?? browserPreviewDependencies();
  const renderer = deps.renderer;
  if (!renderer) {
    return failure('PREVIEW_NOT_READY', 'The off-screen preview is not mounted.');
  }
  const scene: AnimationRenderScene = { animation: readAnimation(state), revision };

  let rendered: RenderedPreview | PreviewResult;
  try {
    rendered = await withRenderDeadline(
      ANIMATION_PREVIEW_TIMEOUT_MS[mode],
      signal,
      (renderSignal) =>
        renderMode(mode, args, scene, input.templateId, { ...deps, renderer }, renderSignal),
    );
  } catch (error) {
    return renderFailure(error, signal);
  }
  if ('structuredContent' in rendered) {
    return rendered;
  }
  if (args.failOnAssetWarnings === true && rendered.warnings.length > 0) {
    return failure('PREVIEW_ASSETS_UNRESOLVED', 'Some fonts or images could not be embedded.', {
      warnings: rendered.warnings,
    });
  }
  const after = readWorkingRevision(input.readCurrentState());
  if (after !== revision) {
    return failure(
      'STALE_REVISION',
      `The working copy moved from revision ${revision} to ${after} while the preview rendered. Render again.`,
      { renderedRevision: revision, workingRevision: after },
    );
  }
  return mcpToolResult(
    toolSuccess({
      readType: 'preview',
      mode,
      workingRevision: revision,
      ...rendered.data,
      ...(rendered.warnings.length > 0 ? { warnings: rendered.warnings } : {}),
    }),
    rendered.attachment,
  );
}
